import AdminPage from '@cohort/merchants/components/AdminPage';
import Button from '@cohort/merchants/components/buttons/Button';
import ExportCSV from '@cohort/merchants/components/buttons/ExportCSV';
import DigitalAssetCollectionOwnersBadge from '@cohort/merchants/components/digital-assets/DigitalAssetCollectionOwnersBadge';
import {
  digitalAssetCollectionsKeys,
  useDigitalAssetCollectionOwnersView,
} from '@cohort/merchants/hooks/api/DigitalAssetCollections';
import {useCurrentDigitalAssetCollection} from '@cohort/merchants/hooks/contexts/currentDigitalAssetCollection';
import usePageSearchParam from '@cohort/merchants/hooks/pageSearchParam';
import {useUserSessionStore} from '@cohort/merchants/hooks/stores/userSession';
import {exportCollectionOwners} from '@cohort/merchants/lib/api/DigitalAssetCollections';
import DigitalAssetCollectionAirdropModal from '@cohort/merchants/pages/digital-asset-collections/digital-asset-collection/owners/DigitalAssetCollectionAirdropModal';
import DigitalAssetCollectionOwnersTable from '@cohort/merchants/pages/digital-asset-collections/digital-asset-collection/owners/DigitalAssetCollectionOwnersTable';
import {PaperPlaneRight} from '@phosphor-icons/react';
import {useQueryClient} from '@tanstack/react-query';
import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

const PAGE_SIZE = 20;

const DigitalAssetCollectionOwnersPage = (): JSX.Element => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const merchantId = useUserSessionStore(store => store.merchantId!);
  const digitalAssetCollection = useCurrentDigitalAssetCollection();
  const queryClient = useQueryClient();
  const {t} = useTranslation('pages', {
    keyPrefix: 'digitalAssetCollections.digitalAssetCollection.owners.page',
  });
  const page = usePageSearchParam();
  const [airdropModalOpen, setAirdropModalOpen] = useState(false);
  const [needPolling, setNeedPolling] = useState(false);

  const {data, isLoading} = useDigitalAssetCollectionOwnersView(
    merchantId,
    digitalAssetCollection.id,
    {
      page,
      pageSize: PAGE_SIZE,
    },
    {
      autoRefresh: needPolling,
    }
  );
  const [pagination, owners] = data ?? [];

  useEffect(() => {
    if (
      owners &&
      digitalAssetCollection.nftEnabled &&
      owners.some(owner => owner.digitalAsset.nft === null)
    ) {
      setNeedPolling(true);
    } else if (needPolling) {
      setNeedPolling(false);
      // we burst cache here to restore pagination computation
      queryClient.invalidateQueries(
        digitalAssetCollectionsKeys.getById(merchantId, digitalAssetCollection.id)
      );
    }
  }, [
    needPolling,
    queryClient,
    merchantId,
    digitalAssetCollection.id,
    owners,
    digitalAssetCollection.nftEnabled,
  ]);

  const headerConfig = {
    title: t('title'),
    titleBadge: <DigitalAssetCollectionOwnersBadge ownersCount={pagination?.total ?? 0} />,
    subtitle: t('subtitle'),
    topRightElements: (
      <Button key="airdrop-btn" onClick={() => setAirdropModalOpen(true)}>
        <PaperPlaneRight className="-ml-1 mr-2 h-5 w-5 -rotate-90" />
        {t('airdropBtn')}
      </Button>
    ),
  };

  const paginationConfig = {
    pagination,
  };

  return (
    <AdminPage header={headerConfig} pagination={paginationConfig}>
      <div className="flex items-center justify-end">
        <ExportCSV
          disabled={isLoading || (owners?.length ?? 0) === 0}
          downloadFunction={async () =>
            exportCollectionOwners(merchantId, digitalAssetCollection.id, 'csv')
          }
        />
      </div>
      <DigitalAssetCollectionOwnersTable
        owners={owners ?? []}
        isLoading={isLoading}
        nftEnabled={digitalAssetCollection.nftEnabled}
        emptyState={{
          isEmpty: owners === undefined || owners.length === 0,
          title: t('emptyStateTitle'),
          description: t('emptyStateDescription'),
        }}
      />
      {airdropModalOpen && (
        <DigitalAssetCollectionAirdropModal onClose={() => setAirdropModalOpen(false)} />
      )}
    </AdminPage>
  );
};

export default DigitalAssetCollectionOwnersPage;
